import { baseApi } from "api/BaseApi";
import { PartialWith } from "api/common/utils/type.utils";
import {
  ProductFormValues,
  FinalFormTypes,
  DraftUpdateTypes,
} from "components/Products/ProductManager/ProductForm/ProductFormRewrite/ProductForm.schema";
import { getISOCurrentDate } from "utils/timeUtils";

import {
  Product,
  ProductStub,
  ProductMetrics,
  CCTLProductMetrics,
  CertificateData,
} from "./product.types";
import { PRODUCT_STATUSES } from "./productStatuses.type";

const PRODUCT_BASE_QUERY = "/project/product";

interface GetProductsQueryParams {
  in_eval?: boolean;
  without_nist?: boolean;
}

//TODO: remove this after testing
export const logFormData = (formData: FormData) => {
  const obj: Record<string, any> = {};

  for (const [key, value] of formData.entries()) {
    if (value instanceof File) {
      obj[key] = {
        name: value.name,
        type: value.type,
        size: value.size,
      };
    } else {
      obj[key] = value;
    }
  }
};

const updateProductBody = ({
  submissionType,
  protectionProfiles,
  csfc,
  nist,
  ears,
  ...product
}: ProductFormValues) => {
  const newMappedProduct = {
    ...product,
    ...nist,
    ...ears,
    //we convert the strings in previous_vid to numbers
    previous_vid: submissionType.previous_vid?.map((vid) => vid.value) || [],
    //we convert tech_type array to just a string with commas
    tech_type: product.tech_type?.map((tech) => tech.value).join(","),
    //we convert protection_profile array to just a string with commas
    protection_profile:
      protectionProfiles.protection_profile?.map((profile) => profile.value) ||
      [],
    sbom_submitted: protectionProfiles.sbom_submitted,
    csfc_eligible: csfc.csfc_eligible,
    csfc_components:
      csfc.csfc_components?.map((component) => component.value) || [],
    publish_status: submissionType.is_resubmission
      ? "Unpublished"
      : product.publish_status,
    submission_type: "Evaluation",
    pre_eval_status:
      product.status === PRODUCT_STATUSES.DRAFT ? null : "Proposed",
    draft_date:
      product.status === PRODUCT_STATUSES.DRAFT ? getISOCurrentDate() : null,
    check_in_date:
      product.status === PRODUCT_STATUSES.PRE_PROPOSAL
        ? getISOCurrentDate()
        : null,
    is_resubmission: submissionType.is_resubmission,
  };

  return newMappedProduct;
};

/**
 * Append every element of `list` as its own part.
 * If the field is present but EMPTY, append one empty string ("") so the
 * key exists in the request and the server can interpret that as “clear all”.
 * If the field is truly omitted (undefined) we do nothing → leave unchanged.
 */
const appendList = (fd: FormData, key: string, list?: unknown[]) => {
  if (list === undefined) return; // leave untouched
  if (list.length === 0) {
    fd.append(key, ""); // explicit “empty”
  } else {
    list.forEach((item) => fd.append(key, String(item)));
  }
};

const createFormData = (product: ProductFormValues) => {
  const formData = new FormData();
  const mappedProduct = updateProductBody(product);

  Object.entries(mappedProduct).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      appendList(formData, key, value);
    } else if (value !== undefined && value !== null && value !== "") {
      formData.append(key, String(value));
    }
  });

  logFormData(formData);
  return formData;
};

const productApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getProduct: build.query<Product, number>({
      query: (id) => `${PRODUCT_BASE_QUERY}/${id}/`,
    }),

    getProductByVid: build.query<Product, number | string>({
      query: (vid) => ({
        url: `${PRODUCT_BASE_QUERY}/get_product_by_vid/`,
        params: { vid },
      }),
    }),

    getAllProducts: build.query<ProductStub[], GetProductsQueryParams | void>({
      query: (params) => {
        const queryParams = params
          ? { in_eval: params.in_eval, without_nist: params.without_nist }
          : {};
        return {
          url: `${PRODUCT_BASE_QUERY}/get_all_products/`,
          params: queryParams,
        };
      },
    }),

    getValidatorProducts: build.query<ProductStub[], void>({
      query: () => `${PRODUCT_BASE_QUERY}/all_my_validations/`,
    }),

    getProductMetrics: build.query<ProductMetrics, void>({
      query: () => `${PRODUCT_BASE_QUERY}/product_metrics/`,
    }),

    getCCTLProductMetrics: build.query<CCTLProductMetrics, void>({
      query: () => `${PRODUCT_BASE_QUERY}/cctl_product_metrics/`,
    }),

    getCCTLProductsAvailableForAM: build.query<Product[], void>({
      query: () => `${PRODUCT_BASE_QUERY}/my_available_am_products/`,
    }),

    getCertificateData: build.query<CertificateData, number>({
      query: (product_id) => ({
        url: `${PRODUCT_BASE_QUERY}/get_certificate_data/`,
        params: { product_id },
      }),
    }),

    createDraftCertificate: build.mutation<void, { product_id: number }>({
      query: (body) => ({
        url: `${PRODUCT_BASE_QUERY}/save_draft_certificate/`,
        method: "POST",
        body,
      }),
    }),

    productUpdate: build.mutation<Product, PartialWith<Product, "product_id">>({
      query: (product) => ({
        url: `${PRODUCT_BASE_QUERY}/${product.product_id}/`,
        method: "PUT",
        body: product,
      }),
    }),

    productCreateAsFormData: build.mutation<Product, ProductFormValues>({
      query: (product) => ({
        url: `${PRODUCT_BASE_QUERY}/`,
        method: "POST",
        body: createFormData(product),
        formData: true,
      }),
    }),

    productUpdateAsFormData: build.mutation<
      Product,
      FinalFormTypes | DraftUpdateTypes
    >({
      query: (product) => ({
        url: `${PRODUCT_BASE_QUERY}/${product.product_id}/`,
        method: "PUT",
        body: createFormData(product),
        formData: true,
      }),
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetAllProductsQuery,
  useGetProductQuery,
  useGetValidatorProductsQuery,
  useLazyGetProductByVidQuery,
  useGetProductMetricsQuery,
  useGetCCTLProductMetricsQuery,
  useGetCCTLProductsAvailableForAMQuery,
  useProductUpdateMutation,
  useProductCreateAsFormDataMutation,
  useProductUpdateAsFormDataMutation,
  useGetCertificateDataQuery,
  useCreateDraftCertificateMutation,
} = productApi;
